// V7.9 - Connection Status Badge
// Shows online/offline/syncing status in the Dashboard header
// Does NOT alter any existing component — purely additive

import { useState, useEffect } from 'react';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { useOfflineMode } from '@/hooks/useOfflineMode';

export function ConnectionStatusBadge() {
  const { isOnline, connectionStatus, pendingCount, triggerSync } = useOfflineMode();
  const [showToast, setShowToast] = useState(false);
  const [prevOnline, setPrevOnline] = useState(isOnline);

  // Show transition toast when status changes
  useEffect(() => {
    if (prevOnline !== isOnline) {
      setShowToast(true);
      const timer = setTimeout(() => setShowToast(false), 4000);
      setPrevOnline(isOnline);
      return () => clearTimeout(timer);
    }
  }, [isOnline, prevOnline]);

  const statusConfig = {
    online: {
      icon: Wifi,
      label: 'Online',
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/20',
      dot: 'bg-emerald-400',
    },
    offline: {
      icon: WifiOff,
      label: 'Offline',
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-500/20',
      dot: 'bg-red-400',
    },
    syncing: {
      icon: RefreshCw,
      label: 'Sincronizando',
      color: 'text-amber-400',
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/20',
      dot: 'bg-amber-400',
    },
  };

  const config = statusConfig[connectionStatus as keyof typeof statusConfig] || statusConfig.online;
  const Icon = config.icon;

  return (
    <div className="relative">
      {/* Main badge */}
      <button
        onClick={() => {
          if (pendingCount > 0 && isOnline) {
            triggerSync();
          }
        }}
        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-all ${config.bg} ${config.border} ${config.color} ${pendingCount > 0 && isOnline ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
        title={
          connectionStatus === 'offline'
            ? 'Sem conexão — modo offline ativo'
            : connectionStatus === 'syncing'
            ? `Sincronizando ${pendingCount} item(ns)...`
            : pendingCount > 0
            ? `${pendingCount} item(ns) pendente(s) — clique para sincronizar`
            : 'Conectado ao servidor'
        }
      >
        {/* Animated dot */}
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot} ${connectionStatus === 'syncing' ? 'animate-pulse' : ''}`} />
        <Icon className={`w-3 h-3 ${connectionStatus === 'syncing' ? 'animate-spin' : ''}`} />
        <span>{config.label}</span>
        {pendingCount > 0 && (
          <span className="ml-0.5 px-1.5 py-0.5 rounded-full bg-white/10 text-[10px]">
            {pendingCount}
          </span>
        )}
      </button>

      {/* Transition toast */}
      {showToast && (
        <div
          className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap z-50 shadow-lg border ${
            isOnline
              ? 'bg-emerald-950 border-emerald-500/30 text-emerald-300'
              : 'bg-red-950 border-red-500/30 text-red-300'
          }`}
          style={{ animation: 'fadeInDown 0.3s ease-out' }}
        >
          {isOnline ? '✓ Conexão restaurada' : '⚠ Modo offline ativo'}
        </div>
      )}
    </div>
  );
}
